import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import ConfirmationModal from "src/components/reusable/ConfirmationModal"; // adjust import path
import CampaignTable from "src/components/reusable/CampaignTable/CampaignTable";
import CustomPagination from "../../../../components/reusable/ReusableTable/CustomPagination";
import AudienceBarChart from "./AudienceBarChart";
import {CustomIcon} from "../../../../components/reusable/CustomIcon";
import { Archive, CirclePlus, X } from "lucide-react";
import {
  getCustomLists,
  getTotalMembers,
  deleteCustomList,
  updateCustomListStatus,
getCustomListCSV
} from "src/services/AudienceListAPIs";
import Papa from "papaparse";
import { handleBulkActionConfirm } from "./Audiencehandlers";
import { CustomList } from "../../EngagementInterfaces";
import "tippy.js/dist/tippy.css";
import { saveAs } from "file-saver";
import ExportConfirmationModal from "src/components/reusable/ExportConfirmationModal/ExportConfirmationModal";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledInput from "src/components/reusable/StyledInput";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/useLanguage";
import { formatNumber, getNumberLocale } from "src/utils/numberFormat";
import { useDynamicTextTranslator } from "src/utils/dynamicTextTranslation";

dayjs.extend(customParseFormat);

const AudienceScreen: React.FC = () => {
  const { t: i18nT, i18n } = useTranslation();
  useLanguage();
  const t = useMemo(
    () =>
      new Proxy({} as Record<string, string>, {
        get: (_, prop: string) => i18nT(`audienceOverview.${String(prop)}`),
      }),
    [i18nT]
  );
  const navigate = useNavigate();
  const numberLocale = useMemo(
    () => getNumberLocale(i18n.language),
    [i18n.language]
  );
  const formatLocalizedNumber = useCallback(
    (value: number, options: Intl.NumberFormatOptions = {}) =>
      formatNumber(value, options, i18n.language),
    [i18n.language]
  );
  const formatLocalizedDate = useCallback(
    (value: string) => {
      if (!value) {return "-";}
      const parsedDate = dayjs(value, "MMM D, YYYY [at] hh:mm A");
      const fallbackDate = new Date(value);
      const date = parsedDate.isValid() ? parsedDate.toDate() : fallbackDate;
      if (Number.isNaN(date.getTime())) {return value;}
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dateText = `${dd}-${mm}-${date.getFullYear()}`;
      const timeText = new Intl.DateTimeFormat(numberLocale, {
        hour: "numeric",
        minute: "2-digit",
      }).format(date);
      return `${dateText}\n${timeText}`;
    },
    [numberLocale]
  );
  const renderBannerWithBoldNumbers = (text: string) => {
    const boldWords = ["contacts", "subscribers", "جهة اتصال", "مشتركون"];
    const escapedWords = boldWords.map(word =>
      word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    );
    const tokenRegex = new RegExp(`(\\d[\\d,]*|${escapedWords.join("|")})`, "gi");

    return text.split(tokenRegex).map((part, index) => {
      const isNumber = /^\d[\d,]*$/.test(part);
      const isBoldWord = boldWords.some(
        word => word.toLowerCase() === part.toLowerCase()
      );
      return isNumber || isBoldWord ? <strong key={index}>{part}</strong> : part;
    });
  };
  // Pagination state
const [searchQuery, setSearchQuery] = useState("");
  const [listPerPage] = useState<number>(7);
  const [totalMembers, setTotalMembers] = useState<number | null>(null);
  const [data, setData] = useState<CustomList[]>([]);
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [, setRemoveLoading] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [listOrSegPresent, setListOrSegPresent] = useState(true);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [showBanner, setShowBanner] = useState(true);
  const [actionType, setActionType] = useState<
    "archive" | "unarchive" | "delete" | null
  >(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedLists, setSelectedLists] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState<string>("members");
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

const campaignColors = [
"#03045E", "#0077B6", "#00B4D8", "#FF9100", "#FF6D00"
];

const handleSortChange = (value: string) => {
  setSortBy(value);
};

const formatTitle = (key: string) => {
  switch (key) {
    case "members":
      return t.members;
    default:
      return key;
  }
};

  // 1. Filter first (based on archive status)
const filteredLists = useMemo(() => {
  return data
    .filter(list =>
      showArchived ? list.status === "archive" : list.status !== "archive"
    );
}, [data, showArchived]);

const dynamicTextPool = useMemo(
  () =>
    data.flatMap(list => [
      list?.list_name,
      list?.list_type,
      list?.created_by,
      ...(list?.tags || []),
    ]),
  [data]
);

const translateDynamicLabel = useDynamicTextTranslator(
  dynamicTextPool,
  i18n.language
);

const searchableLists = useMemo(() => {
  const normalizedQuery = searchQuery.toLowerCase();
  if (!normalizedQuery) {
    return filteredLists;
  }

  return filteredLists.filter(list => {
    const translatedName = translateDynamicLabel(list.list_name) || "";
    const translatedType = translateDynamicLabel(list.list_type) || "";
    const translatedCreatedBy = translateDynamicLabel(list.created_by) || "";
    const translatedTags = (list.tags || [])
      .map(tag => translateDynamicLabel(tag))
      .join(" ");

    return (
      translatedName.toLowerCase().includes(normalizedQuery) ||
      translatedType.toLowerCase().includes(normalizedQuery) ||
      translatedCreatedBy.toLowerCase().includes(normalizedQuery) ||
      translatedTags.toLowerCase().includes(normalizedQuery)
    );
  });
}, [filteredLists, searchQuery, translateDynamicLabel]);

  const sortedLists = useMemo(() => {
    return [...searchableLists].sort((a, b) => {
      const format = "MMM D, YYYY [at] hh:mm A"; // e.g., Jul 11, 2025 at 10:28 AM
      const dateA = dayjs(a.created_at, format).valueOf();
      const dateB = dayjs(b.created_at, format).valueOf();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
  }, [searchableLists, sortOrder]);

  // 2. Then paginate only the filtered data
  const paginatedLists = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedLists.slice(start, end);
  }, [sortedLists, currentPage, pageSize]);

// Compute Data Science Lists safely
const dataScienceLists = useMemo(() => {
  return data
    .filter(list => list?.list_type === "Data Science") // safe check
    .map(list => ({
      campaign_name: translateDynamicLabel(list?.list_name ?? t.unnamedList) || t.unnamedList, // fallback name
      members: list?.members ?? 0, // fallback to 0
    }));
}, [data, t.unnamedList, translateDynamicLabel]);


  useEffect(() => {
    setCurrentPage(1);
  }, [searchableLists]);

  const indexOfLastList = currentPage * listPerPage;

  useEffect(() => {
    fetchCustomLists();
  }, [i18n.resolvedLanguage, i18n.language]);

  useEffect(() => {
    const fetchTotalMembers = async () => {
      try {
        const res = await getTotalMembers();
        setTotalMembers(res.total_members);
      } catch (error) {
        if (error instanceof Error) {
        } else {
        }
      }
    };

    fetchTotalMembers();
  }, [i18n.resolvedLanguage, i18n.language]);

  const getTitle = () => {
    const base = selectedItem ? t.listsSegmentSingular : t.listsSegmentPlural;
    switch (actionType) {
      case "delete":
        return `${t.delete} ${base}`;
      case "archive":
        return `${t.archive} ${base}`;
      case "unarchive":
        return `${t.unarchive} ${base}`;
      default:
        return "";
    }
  };

  const getActionLabel = (action: "archive" | "unarchive" | "delete" | null) => {
    if (action === "archive") {return t.archive;}
    if (action === "unarchive") {return t.unarchive;}
    if (action === "delete") {return t.delete;}
    return "";
  };

  const getDescription = () => {
    const actionLabel = getActionLabel(actionType);
    if (selectedItem) {
      return t.areYouSureActionOne
        .replace("{{action}}", actionLabel)
        .replace("{{item}}", selectedItem);
    }
    return t.areYouSureActionMany
      .replace("{{action}}", actionLabel)
      .replace("{{count}}", String(selectedLists.length));
  };

  const getIcon = () => {
    switch (actionType) {
      case "delete":
        return "/Icons/delete-icon.svg";
      case "archive":
        return "/Icons/archive-icon.svg";
      case "unarchive":
        return "/Icons/unarchive-icon.svg";
      default:
        return "";
    }
  };

  const fetchCustomLists = async () => {
    try {
      setLoading(true);
      const data = await getCustomLists();

      if (data.status === "success") {
        const lists = data.custom_lists || [];
        setData(lists);
        setListOrSegPresent(lists.length > 0);
      } else {
        setListOrSegPresent(false);
      }
    } catch (err) {
      console.error(t.errorFetchingCustomLists, err);
      setListOrSegPresent(false);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveList = (list: CustomList) => {
    setSelectedItem(list.list_name);
    setSelectedListId(list.custom_list_id);
    setActionType(list.status === "archive" ? "unarchive" : "archive");
    // Store current selections but don't use them for individual action
    setModalOpen(true);
  };

  const handleDeleteClick = (listId: number, name: string) => {
    setSelectedItem(name);
    setSelectedListId(listId);
    setActionType("delete");
    // Store current selections but don't use them for individual action
    setModalOpen(true);
  };
  const handleConfirm = async () => {
    if (!selectedListId || !actionType) {return;}

    setActionLoading(true);

    try {
      const resolvedActionType =
        actionType === "unarchive" ? "active" : actionType;
      const data = await updateCustomListStatus(
        selectedListId,
        resolvedActionType as "archive" | "active"
      );

      if (data.status === "success") {
        message.success(
          actionType === "archive"
            ? t.listArchivedSuccess.replace("{{item}}", selectedItem || "")
            : t.listUnarchivedSuccess.replace("{{item}}", selectedItem || "")
        );
        fetchCustomLists();
      } else {
        message.error(
          t.failedToActionList.replace("{{action}}", getActionLabel(actionType))
        );
      }
    } catch (err) {
      console.error(t.errorActioningList.replace("{{action}}", getActionLabel(actionType)), err);
      message.error(
        t.errorActioningList.replace("{{action}}", getActionLabel(actionType))
      );
    } finally {
      setActionLoading(false);
      setModalOpen(false);
      setSelectedItem(null);
      setSelectedListId(null);
      setActionType(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedListId) {return;}

    setRemoveLoading(true);
    try {
      const data = await deleteCustomList(selectedListId);

      if (data.status === "success") {
        message.success(
          t.listDeletedSuccess.replace("{{item}}", selectedItem || "")
        );
        setData(prev =>
          prev.filter(item => item.custom_list_id !== selectedListId)
        );
      } else {
        message.error(t.failedToDeleteListSegment);
      }
    } catch (err) {
      console.error(t.errorDeletingListSegment, err);
      message.error(t.errorDeletingListSegment);
    } finally {
      setRemoveLoading(false);
      setModalOpen(false);
      setSelectedItem(null);
      setSelectedListId(null);
      setActionType(null);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedLists(
        searchableLists
          .filter(list => list.list_type !== "Data Science") // exclude Data Science
          .map(list => list.custom_list_id)
      );
    } else {
      setSelectedLists([]);
    }
  };

 const handleSelectList = (listId: number, checked: boolean) => {
  if (checked) {
    setSelectedLists(prev => [...prev, listId]);
  } else {
    setSelectedLists(prev => prev.filter(id => id !== listId));
  }
};

  const handleBulkAction = (action: "archive" | "unarchive" | "delete") => {
    if (selectedLists.length === 0) {
      message.warning(t.pleaseSelectAtLeastOneList);
      return;
    }
    setActionType(action);
    // Clear individual selection for bulk action
    setSelectedItem(null);
    setSelectedListId(null);
    setModalOpen(true);
  };
 // 1️⃣ Export a single list directly
const exportSingleList = async (listId: number) => {
  try {
    message.loading({ content: t.exportingList, key: "export" });

    const blob = await getCustomListCSV(listId);
    const text = await blob.text();
    const { data } = Papa.parse(text, { header: true });
    const csv = Papa.unparse(data);
    const csvBlob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const listName =
      filteredLists.find(l => l.custom_list_id === listId)?.list_name ||
      t.audienceListFallback;

    saveAs(csvBlob, `${listName}.csv`);
    message.success({ content: t.listExportedSuccess, key: "export" });
  } catch (error) {
    console.error("Export failed:", error);
    message.error({ content: t.failedToExportList, key: "export" });
  }
};

// 2️⃣ Handle Export button click
const handleExportClick = () => {
  if (selectedLists.length === 0) {
    message.warning(t.pleaseSelectAtLeastOneAudienceList);
    return;
  }

  if (selectedLists.length === 1) {
    // Single list → export directly
    exportSingleList(selectedLists[0]);
  } else {
    // Multiple → show modal
    setExportModalOpen(true);
  }
};

const exportCombinedLists = async () => {
  message.loading({ content: t.exportingCombinedData, key: "export" });
  try {
    const allData: any[] = [];

    for (const listId of selectedLists) {
      const blob = await getCustomListCSV(listId);
      const text = await blob.text();
      const { data } = Papa.parse(text, { header: true });
      data.forEach((row: any) => {
        row.__ListName__ =
          filteredLists.find(l => l.custom_list_id === listId)?.list_name || "";
      });
      allData.push(...data);
    }

    const csv = Papa.unparse(allData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    // Build a readable file name (e.g., Likely_to_churn_6_months_combined.csv)
    const firstName =
      filteredLists.find(l => l.custom_list_id === selectedLists[0])?.list_name || t.audienceDataFallback;
    const fileName =
      selectedLists.length > 1
        ? `${firstName}_and_${selectedLists.length - 1}_more_combined.csv`
        : `${firstName}.csv`;

    saveAs(blob, fileName);
    message.success({ content: t.combinedExportSuccess, key: "export" });
  } catch (error) {
    console.error("Export failed:", error);
    message.error({ content: t.failedToExportData, key: "export" });
  } finally {
    setExportModalOpen(false);
  }
};

const exportSeparateLists = async () => {
  message.loading({ content: t.exportingListsSeparately, key: "export" });
  try {
    for (const listId of selectedLists) {
      const blob = await getCustomListCSV(listId);
      const text = await blob.text();
      const { data } = Papa.parse(text, { header: true });
      const csv = Papa.unparse(data);
      const blobFile = new Blob([csv], { type: "text/csv;charset=utf-8;" });

      const listName =
        filteredLists.find(l => l.custom_list_id === listId)?.list_name || t.audienceListFallback;
      saveAs(blobFile, `${listName}.csv`);
    }
    message.success({ content: t.separateExportsCompleted, key: "export" });
  } catch (error) {
    console.error("Export failed:", error);
    message.error({ content: t.failedToExportLists, key: "export" });
  } finally {
    setExportModalOpen(false);
  }
};


  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <LoadingSpinner />
      </div>
    );
  }
  const columns = [
    {
      title: (
        <input
          type="checkbox"
          checked={selectAll}
          onChange={e => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: "checkbox",
      key: "checkbox",
      render: (_: any, record: any) => (
       <input
  type="checkbox"
  checked={selectedLists.includes(record.custom_list_id)}
  onChange={e =>
    handleSelectList(record.custom_list_id, e.target.checked)
  }
  onClick={e => e.stopPropagation()}
/>
      ),
      width: 50,
    },
{
  title: t.name,
  dataIndex: "list_name",
  key: "name",
  className: "list-name",
  render: (text: string) => (
    <span className="campaign-name" dir="auto" style={{ unicodeBidi: "plaintext" }}>
      {translateDynamicLabel(text) || text}
    </span>
  ),
},
    {
      title: t.type,
      dataIndex: "list_type",
      key: "type",
      render: (text: string) => (
        <span dir="auto" style={{ unicodeBidi: "plaintext" }}>
          {translateDynamicLabel(text) || text}
        </span>
      ),
    },
    {
      title: t.members,
      dataIndex: "members",
      key: "members",
      render: (value: number | string) => {
        const numericValue = typeof value === "number" ? value : Number(value);
        return Number.isFinite(numericValue) ? formatLocalizedNumber(numericValue) : value;
      },
    },
    {
      title: t.created,
      dataIndex: "created_at",
      key: "created",
      render: (value: string) => (
        <div style={{ whiteSpace: "pre-line" }}>
          {formatLocalizedDate(value)}
        </div>
      ),
    },
    {
      title: t.createdBy,
      dataIndex: "created_by",
      key: "createdBy",
      render: (text: string) => (
        <span dir="auto" style={{ unicodeBidi: "plaintext" }}>
          {translateDynamicLabel(text) || text}
        </span>
      ),
    },
{
  title: t.tags,
  dataIndex: "tags",
  key: "tags",
  render: (tags: string[]) => (
    <div className="tags-container">
      {(tags || []).map((tag, index) => (
        <span key={index} className="tag-item">
          {translateDynamicLabel(tag) || tag}
        </span>
      ))}
    </div>
  ),
},
    {
      title: t.actions,
      key: "actions",
      render: (_: any, item: any) => {
        const isDataScience = item.list_type === "Data Science";

        if (isDataScience) {
          return (
            <div className="table-row-actions">
              
              <CustomIcon
                name="view"
                tooltip={t.view}
                onClick={() =>
                  navigate("/account/engagement/modify-file", {
                    state: {
                      listId: item.custom_list_id,
                      listName: item.list_name,
                      data_science: true,
                    },
                  })
                }
              />
            </div>
          );
        }

        return !showArchived ? (
          <div className="table-row-actions">
            <CustomIcon
              name="edit"
              tooltip={t.edit}
              onClick={() =>
                navigate("/account/engagement/modify-file", {
                  state: {
                    listId: item.custom_list_id,
                    listName: item.list_name,
                    data_science: false,
                  },
                })
              }
            />
            <CustomIcon
              name="archive"
              tooltip={t.archive}
              onClick={() => handleArchiveList(item)}
            />
            <CustomIcon
              name="delete"
              tooltip={t.delete}
              onClick={() => handleDeleteClick(item.custom_list_id, item.list_name)}
            />
            <CustomIcon
                name="download"
                tooltip={t.exportData}
                onClick={() => exportSingleList(item.custom_list_id)}
              />
          </div>
        ) : (
          <div className="table-row-actions">
            <CustomIcon
              name="download"
              tooltip={t.exportData}
              onClick={() => exportSingleList(item.custom_list_id)}
            />
            <CustomIcon
              name="unarchive"
              tooltip={t.unarchive}
              onClick={() => handleArchiveList(item)}
            />
          </div>
        );
      },
    },
  ];

  const hasSelection = selectedLists.length > 0;
  const hasDataScienceSelection = selectedLists.some(listId =>
    data.some(item => item.custom_list_id === listId && item.list_type === "Data Science")
  );

  return (
    <div className="campaign-dashboard">
      {showBanner && (
        <div className="info-banner-row">
          <div className="info-banner-audience">
            <span>
              {renderBannerWithBoldNumbers(
                t.bannerText.replace(
                  "{{count}}",
                  totalMembers !== null ? formatLocalizedNumber(totalMembers) : t.loading
                )
              )}
            </span>
            <button
              type="button"
              className="info-banner-close"
              aria-label={t.dismissSummary}
              onClick={() => setShowBanner(false)}
            >
            <X size={12}/>
            </button>
          </div>
        </div>
      )}
                      <p className="Campaign-dashboard-header">{t.audienceOverview}</p>
    <div className="overview-summary">
      <span>{t.trackGrowth}</span>
    </div>
       

      <div className="list-section">
        <div className="custom-list-header audience-list-header">
          <span className="headline list-header-title">
            {showArchived ? t.archivedListsAndSegments : t.activeListsAndSegments}
          </span>

          <div className="header-actions-buttons">
            {!showArchived && (
              <button
                className="button button--primary audience-btn"
                onClick={() => navigate("/account/engagement/select-list-or-segment")}
              >
                <CirclePlus size={16}/>
                {t.createListSegment}
              </button>
            )}
            <button
              className={`toggle-pill ${showArchived ? "is-on" : "is-off"}`}
              type="button"
              aria-label={showArchived ? t.viewActiveLists : t.viewArchivedLists}
              onClick={() => {
                setShowArchived(!showArchived);
                setSelectedLists([]);
                setSelectAll(false);
              }}
            >
              <span className="toggle-pill__icon">
                {showArchived ? (
                  <img
                    src="/Icons/unarchive-white.svg"
                    alt={t.tooltipUnarchive}
                    className="toggle-pill__img"
                  />
                ) : (
                  <Archive size={16} />
                )}
              </span>
            </button>
          </div>
        </div>

        <div className="audience-toolbar-row">
          <StyledInput
            placeholder={t.searchLists}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="audience-search-input custom-input-audience"
          />

          <div className="audience-actions">
            {!showArchived ? (
              <>
                <CustomIcon
                  name="delete"
                  tooltip={t.deleteSelected}
                  onClick={() =>
                    hasSelection &&
                    !hasDataScienceSelection &&
                    handleBulkAction("delete")
                  }
                  className={`audience-actionbuttons ${(!hasSelection || hasDataScienceSelection) ? "is-disabled" : ""}`}
                />
                <CustomIcon
                  name="archive"
                  tooltip={t.archiveSelected}
                  onClick={() => hasSelection && handleBulkAction("archive")}
                  className={`audience-actionbuttons ${!hasSelection ? "is-disabled" : ""}`}
                />
              </>
            ) : (
              <CustomIcon
                name="unarchive"
                tooltip={t.unarchiveSelected}
                onClick={() => hasSelection && handleBulkAction("unarchive")}
                className={`audience-actionbuttons ${!hasSelection ? "is-disabled" : ""}`}
              />
            )}

            <CustomIcon
              name="download"
              tooltip={t.exportData}
              onClick={() => hasSelection && handleExportClick()}
              className={`audience-actionbuttons ${!hasSelection ? "is-disabled" : ""}`}
            />

            <StyledDropdown
              className="custom-dropdown audience-sort-dropdown"
              value={sortOrder}
              onChange={value => setSortOrder(value as "newest" | "oldest")}
              options={[
                { value: "newest", label: t.newestToOldest },
                { value: "oldest", label: t.oldestToNewest },
              ]}
              popupClassName="custom-dropdown-menu"
            />
          </div>
        </div>


        {listOrSegPresent ? (
          <CampaignTable
            className="audience-list-table"
            columns={columns}
            data={paginatedLists}
            rowKey="custom_list_id"
            pagination={false}
            customPagination={
                <CustomPagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={searchableLists.length}
                  onChange={page => setCurrentPage(page)}
                />
              }
          />
        ) : (
         <div className="no-data-container">
  <img
    src="/Icons/notFound.jpg"
    alt={t.noListsSegmentsFound}
    className="no-data-image"
  />
  <h3>{t.noListsSegmentsFound}</h3>
  <p className="no-data-text">{t.noDataText}</p>
</div>
        )}
      </div>

      <div className="audience-bar-chart-container">
        <AudienceBarChart
          audienceNames={dataScienceLists?.map(item => item?.campaign_name ?? t.unnamedList)}
          data={dataScienceLists ?? []}
          campaignColors={campaignColors}
          formatTitle={formatTitle}
        />
      </div>

        <ConfirmationModal
          modalOpen={modalOpen}
          title={getTitle()}
          description={getDescription()}
          icon={getIcon()}
          confirmText={
            actionLoading
              ? actionType === "delete"
                ? t.deleting
                : actionType === "archive"
                ? t.archiving
                : t.unarchiving
              : actionType === "delete"
              ? t.delete
              : actionType === "archive"
              ? t.archive
              : t.unarchive
          }
          cancelText={t.cancel}
          confirmButtonProps={{
            loading: actionLoading, 
          }}
          onConfirm={async () => {
            setActionLoading(true); 

            try {
              if (selectedItem) {
                if (actionType === "delete") {
                  await handleDeleteConfirm();
                } else {
                  await handleConfirm(); 
                }
              } else {
                await handleBulkActionConfirm(
                  selectedLists,
                  actionType!,
                  fetchCustomLists,
                  (loading: boolean) => {
                    setActionLoading(loading);
                  }
                );
              }
            } catch (error) {
              console.error(error);
            } finally {
              setActionLoading(false); // ✅ Stop loading
              setModalOpen(false);
              setSelectedItem(null);
              setSelectedLists([]);
              setSelectAll(false);
              setActionType(null);
            }
          }}
          onCancel={() => {
            setModalOpen(false);
            setSelectedItem(null);
            setSelectedListId(null);
            setActionType(null);
            setActionLoading(false); // Optional reset
          }}
        />
      <ExportConfirmationModal
  open={exportModalOpen}
  onCancel={() => setExportModalOpen(false)}
  onCombine={exportCombinedLists}
  onSeparate={exportSeparateLists}
  selectedCount={selectedLists.length}
/>
    </div>
  );
};

export default AudienceScreen;
